/**
 *
 * Small Shortcuts functionality
 * some functionality was copied from
 * https://github.com/jaywcjlove/hotkeys/blob/master/src/main.js
 *
 */

import shortid from 'shortid';
import {getCode, parseCode} from '@/common/shortcuts/utils';
import {
    eventTypes, keyMap, modifier, modifierMap,
} from '@/common/shortcuts/constants';

class Shortcut {
    constructor(
        {
            id,
            keyPath,
            callback,
            keyup,
            keydown,
            unregister,
        },
    ) {
        this.id = id;
        // groups of keys which should be pressed
        this.codes = keyPath.split(',').map((key) => {
            if (key.includes('+')) {
                return key.split('+').map(getCode);
            }
            return [getCode(key)];
        });
        this.callback = callback;
        this.keyup = keyup;
        this.keydown = keydown;
        this.unregister = unregister;
    }
}

export default class Shortcuts {
    static instance = new Shortcuts(); // singleton instance

    constructor() {
        if (Shortcuts.instance) {
            return Shortcuts.instance;
        }
        this.onDownReadyCode = null;
        this.active = [];
        this.elements = [
            // {
            //     node: document || element,
            //     shortcuts: [ new Shortcut() ]
            // }
        ];
        Shortcuts.instance = this;

        // clear active list on hidden window event
        window.addEventListener('blur', () => {
            this.active = [];
        });
    }

    handleShortcuts(type, [e, ...args], shortcuts) {
        const active = this.active.slice(0);
        const code = parseCode(e);
        const isMetaKey = modifierMap[code] === 'metaKey' || code === 91;

        const getClearedActiveList = () => {
            if (type !== eventTypes.keyup) {
                return active;
            }
            if (!active.includes(code)) {
                return active;
            }
            if (isMetaKey) {
                // when the meta key is upped,
                // we should to clear all buttons which was pressed after meta
                active.splice(active.indexOf(code), active.length);
                return active;
            }
            active.splice(active.indexOf(code), 1);
            return active;
        };

        if (type === eventTypes.keydown && code === this.onDownReadyCode) {
            // when button is clamped disable double keydown events
            return;
        }

        // on keydown event push new code in active buttons
        if (type === eventTypes.keydown) {
            if (!active.includes(code)) {
                active.push(code);
            }
        }

        // if we haven't shortcuts for event - we still should restore active buttons value
        if (!shortcuts.length) {
            // delete active button on keyup
            this.active = getClearedActiveList();
            this.onDownReadyCode = null; // disable "clamped down button"
            return;
        }

        const activeLength = active.length;

        for (let i = 0; i < shortcuts.length; i += 1) {
            const shortcut = shortcuts[i];
            const suitShortcutCombination = shortcut.codes.find(
                group => group.length === activeLength
                    && active.every(c => group.includes(c)),
            );
            if (suitShortcutCombination) {
                // "no block" UI callback call
                setTimeout(() => shortcut.callback(...[e, ...args]), 0);
            }
        }

        // prepare active list
        this.active = getClearedActiveList();

        if (type === eventTypes.keydown) {
            this.onDownReadyCode = code; // save clamped button on down event
        } else {
            this.onDownReadyCode = null; // disable "clamped down button" on up event
        }
    }

    onKeyDown(data, ...args) {
        return this.handleShortcuts(
            eventTypes.keydown,
            args,
            data.shortcuts.filter(({keydown}) => keydown),
        );
    }

    onKeyUp(data, ...args) {
        return this.handleShortcuts(
            eventTypes.keyup,
            args,
            data.shortcuts.filter(({keyup}) => keyup),
        );
    }

    /**
     *
     * @param {string} key - key string value "a+b+c", "tab", "esc+a", "esc,tab"
     * @param {function} callback
     * @param {element|undefined} [node=document] - document element or undefined
     * @param {boolean|undefined} keyup - handle callback on key up event
     * @param {boolean|undefined} keydown - handle callback on key down event
     * @returns {Shortcut}
     */
    static register(key, callback, {node, keydown, keyup} = {}) {
        const id = `event-${shortid.generate()}`;

        const options = {
            id,
            keyPath: key,
            node: node || window,
            callback,
            keydown: keyup === undefined ? true : !!keyup,
            keyup: keydown === undefined ? false : !!keydown,
        };

        let data = Shortcuts.instance.elements.find(item => item.node === options.node);

        if (!data) {
            const keyDown = function keyDown(...args) {
                Shortcuts.instance.onKeyDown(...[data, ...args]);
            };
            const keyUp = function keyUp(...args) {
                Shortcuts.instance.onKeyUp(...[data, ...args]);
            };
            options.node.addEventListener(eventTypes.keydown, keyDown);
            options.node.addEventListener(eventTypes.keyup, keyUp);

            Shortcuts.instance.elements.push({
                node: options.node,
                destroy: () => {
                    Shortcuts.instance.elements = Shortcuts.instance.elements
                        .filter(item => item.node !== options.node);
                    options.node.removeEventListener(eventTypes.keydown, keyDown);
                    options.node.removeEventListener(eventTypes.keyup, keyUp);
                },
                shortcuts: [],
            });
            data = Shortcuts.instance.elements.find(item => item.node === options.node);
        }

        const shortcut = new Shortcut({
            ...options,
            unregister: () => Shortcuts.instance.unregister(shortcut.id),
        });
        data.shortcuts.push(shortcut);
        return shortcut;
    }

    unregister(shortcutId) {
        for (let i = 0; i < this.elements.length; i += 1) {
            const index = this.elements[i].shortcuts.findIndex(item => item.id === shortcutId);
            if (index >= 0) {
                this.elements[i].shortcuts.splice(index, 1);

                if (!this.elements[i].shortcuts.length) {
                    this.elements[i].destroy();
                }
                break;
            }
        }
    }
}


/**
 * By default only keydown
 *
 * // handle keydown and keyup
 * const shortcut = Shortcuts.up.register(() => console.log('up'), {keydown: true, keyup: true});
 * shortcut.unregister() // !!!! We must unregister shortcut when he is no more needed
 *
 * // AND CONDITION
 * Shortcuts.up.register(() => console.log('up'));
 * Shortcuts.up.down.register(() => console.log('up'))
 * Shortcuts.up.down.esc.register(() => console.log('up.down.esc'))
 *
 * Shortcuts.register('w+e', () => {})
 *
 * // OR CONDITION
 * Shortcuts.register('e', () => {})
 * Shortcuts.register('e,c,d', () => {})
 *
 *
 * // VUE example
 * created(){
 *  this.shortcuts = [
 *      Shortcuts.up.register(() => console.log('up')),
 *      Shortcuts.down.register(() => console.log('down')),
 *      Shortcuts.left.register(() => console.log('left'))
 *  ]
 * },
 * beforeDestroy(){
 *  this.shortcuts.forEach(shortcut => shortcut.unregister());
 * }
 */
const extendRecursiveProperties = (parent, object) => new Proxy(object, {
    get(target, prop) {
        if (target[prop]) {
            return target[prop];
        }
        return extendRecursiveProperties(`${parent}+${prop}`, {
            register: (...args) => Shortcuts.register(...[`${parent}+${prop}`, ...args]),
        });
    },
});

Object.assign(
    Shortcuts,
    Object.keys({...keyMap, ...modifier}).reduce((memo, name) => ({
        ...memo,
        [name]: extendRecursiveProperties(name, {
            register: (...args) => Shortcuts.register(...[name, ...args]),
        }),
    }), {}),
);
